import { useEffect, useState, useMemo, memo } from "react"
import { db } from "../../../utils/firebase"

// ChartJS imports
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend } from "chart.js"

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend)

// Helper function to get color based on year offset
const getColorForYear = (yearOffset) => {
	if (yearOffset === 0) return { border: "#e41f13", bg: "#e41f13" }

	// Different colors for each historical year, with reduced opacity for visibility
	const colors = [
		{ r: 59, g: 130, b: 246 }, // Blue
		{ r: 168, g: 85, b: 247 }, // Purple
		{ r: 34, g: 197, b: 94 }, // Green
		{ r: 251, g: 146, b: 60 }, // Orange
		{ r: 20, g: 184, b: 166 }, // Teal
	]

	const colorIndex = Math.min(yearOffset - 1, colors.length - 1)
	const color = colors[colorIndex]
	const opacity = 0.6 // Consistent opacity for all historical years

	return {
		border: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
		bg: `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`,
	}
}

function BookingsLineChart({ dateRanges, selectedYear, selectedMonth }) {
	const [bookingsByYear, setBookingsByYear] = useState({})
	const [loading, setLoading] = useState(true)

	// Fetch data for all date ranges
	useEffect(() => {
		const fetchAllData = async () => {
			setLoading(true)
			const dataByYear = {}

			for (const range of dateRanges) {
				const bookings = await db
					.collection("bookings")
					.where("created", ">", range.start)
					.where("created", "<", range.end)
					.get()
					.then((bookingDocs) => {
						const bookingsList = []
						bookingDocs.forEach((bookingDoc) => {
							bookingsList.push({ id: bookingDoc.id, ...bookingDoc.data() })
						})
						return { bookings: bookingsList, count: bookingsList.length }
					})

				dataByYear[range.yearOffset] = {
					...bookings,
					year: range.year,
				}
			}

			setBookingsByYear(dataByYear)
			setLoading(false)
		}

		fetchAllData()
	}, [dateRanges])

	// Process bookings data with memoization
	const processBookings = useMemo(() => {
		return (data) => {
			const counts = {}

			data.forEach((booking) => {
				const date = new Date(booking.created.seconds * 1000)
				const normalizedDate = date.toLocaleDateString("en-GB", {
					day: "2-digit",
					month: "2-digit",
				})

				counts[normalizedDate] = (counts[normalizedDate] || 0) + 1
			})

			// Sort dates and calculate cumulative bookings
			const sortedDates = Object.keys(counts).sort((a, b) => {
				const [dayA, monthA] = a.split("/").map(Number)
				const [dayB, monthB] = b.split("/").map(Number)
				return monthA === monthB ? dayA - dayB : monthA - monthB
			})

			const cumulativeCounts = {}
			const dailyCounts = {}
			let runningTotal = 0
			sortedDates.forEach((date) => {
				runningTotal += counts[date]
				cumulativeCounts[date] = runningTotal
				dailyCounts[date] = counts[date]
			})

			return { cumulativeCounts, dailyCounts }
		}
	}, [])

	// Process data for all years with memoization
	const processedData = useMemo(() => {
		const processed = {}
		Object.keys(bookingsByYear).forEach((yearOffset) => {
			processed[yearOffset] = processBookings(bookingsByYear[yearOffset].bookings)
		})
		return processed
	}, [bookingsByYear, processBookings])

	// Get all unique dates with memoization
	const allDates = useMemo(() => {
		const datesSet = new Set()
		Object.values(processedData).forEach((yearData) => {
			Object.keys(yearData.cumulativeCounts).forEach((date) => datesSet.add(date))
		})

		return Array.from(datesSet).sort((a, b) => {
			const [dayA, monthA] = a.split("/").map(Number)
			const [dayB, monthB] = b.split("/").map(Number)
			return monthA === monthB ? dayA - dayB : monthA - monthB
		})
	}, [processedData])

	// Check if we're viewing the current month/year
	const isCurrentMonth = useMemo(() => {
		const now = new Date()
		return selectedYear === now.getFullYear() && selectedMonth === now.getMonth()
	}, [selectedYear, selectedMonth])

	// Get current date for comparison (only used if viewing current month)
	const today = useMemo(() => {
		return new Date().toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "2-digit",
		})
	}, [])

	// Generate datasets with memoization
	const chartData = useMemo(() => {
		const datasets = []

		// Generate a dataset for each year
		Object.keys(bookingsByYear)
			.sort((a, b) => parseInt(a) - parseInt(b))
			.forEach((yearOffset) => {
				const offset = parseInt(yearOffset)
				const yearData = processedData[offset]?.cumulativeCounts || {}
				const color = getColorForYear(offset)
				const year = bookingsByYear[offset].year

				const data = allDates.map((date, index, dates) => {
					// For current year viewing current month, don't show future dates
					if (offset === 0 && isCurrentMonth && date > today) {
						return null
					}

					// If the current date has no value, carry forward the last value
					if (!yearData[date] || yearData[date] === 0) {
						for (let i = index - 1; i >= 0; i--) {
							const prevDate = dates[i]
							if (yearData[prevDate] && yearData[prevDate] !== 0) {
								return yearData[prevDate]
							}
						}
					}

					return yearData[date] || 0
				})

				datasets.push({
					label: offset === 0 ? `${year} (Current)` : `${year}`,
					data,
					borderColor: color.border,
					backgroundColor: color.bg,
					borderWidth: offset === 0 ? 3 : 2,
					pointRadius: offset === 0 ? 3 : 1,
					pointHoverRadius: offset === 0 ? 5 : 3,
					borderDash: offset === 0 ? [] : [5, 5],
					fill: false,
					tension: 0.1,
				})
			})

		return { labels: allDates, datasets }
	}, [bookingsByYear, processedData, allDates, today, isCurrentMonth])

	// Chart options with memoization
	const chartOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			animations: {
				y: {
					easing: "easeOutQuart",
					duration: 1500,
				},
				x: {
					duration: 0,
				},
			},
			plugins: {
				legend: {
					position: "top",
					labels: {
						usePointStyle: true,
						padding: 15,
						font: {
							size: 12,
							weight: "500",
						},
					},
				},
				tooltip: {
					enabled: true,
					mode: "index",
					intersect: false,
					backgroundColor: "rgba(255, 255, 255, 0.95)",
					titleColor: "#111827",
					bodyColor: "#374151",
					borderColor: "#e5e7eb",
					borderWidth: 1,
					padding: 12,
					boxPadding: 6,
					usePointStyle: true,
					callbacks: {
						label: function (context) {
							const value = context.raw
							const date = context.label

							// Skip if value is null
							if (value === null || value === undefined) {
								return null
							}

							// Get the daily total for this date and dataset
							const yearOffset = context.datasetIndex
							const dailyData = processedData[yearOffset]?.dailyCounts || {}
							const dailyTotal = dailyData[date] || 0

							// Compare with current year if not current year
							if (context.datasetIndex !== 0 && chartData.datasets[0]) {
								const currentYearValue = chartData.datasets[0].data[context.dataIndex]

								// Skip comparison if current year value is null or undefined
								if (currentYearValue === null || currentYearValue === undefined) {
									return [`${context.dataset.label}: ${value}`, `Daily Total: ${dailyTotal}`]
								}

								const diff = currentYearValue - value
								const percentageDiff = value ? ((diff / value) * 100).toFixed(1) : "N/A"
								return [`${context.dataset.label}: ${value} (${diff >= 0 ? "+" : ""}${percentageDiff}% vs current)`, `Daily Total: ${dailyTotal}`]
							}

							return [`${context.dataset.label}: ${value}`, `Daily Total: ${dailyTotal}`]
						},
					},
				},
			},
			interaction: {
				mode: "index",
				intersect: false,
			},
			scales: {
				x: {
					title: {
						display: true,
						text: "Date (DD/MM)",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						maxRotation: 45,
						minRotation: 45,
						font: { size: 10 },
						color: "#6b7280",
					},
				},
				y: {
					title: {
						display: true,
						text: "Cumulative Bookings",
						font: { size: 12, weight: "600" },
						color: "#374151",
					},
					grid: {
						color: "rgba(229, 231, 235, 0.5)",
						drawBorder: false,
					},
					ticks: {
						font: { size: 10 },
						color: "#6b7280",
					},
				},
			},
		}),
		[chartData]
	)

	if (loading) {
		return (
			<div>
				<h2>Booking Trends</h2>
				<p>Loading data...</p>
			</div>
		)
	}

	return (
		<div>
			<h2>Booking Trends</h2>
			{Object.keys(bookingsByYear)
				.sort((a, b) => parseInt(a) - parseInt(b))
				.map((yearOffset) => {
					const offset = parseInt(yearOffset)
					const data = bookingsByYear[offset]
					return (
						<p key={offset}>
							{offset === 0 ? "Current" : `${data.year}`}: {data.count}
						</p>
					)
				})}
			<br />
			<div style={{ height: "600px", width: "100%" }}>
				<Line
					data={chartData}
					options={chartOptions}
				/>
			</div>
		</div>
	)
}

export default memo(BookingsLineChart)
